import React from "react";
import { colors } from "../../styles/colors";
import { DAY_ABBREVS } from "../../constants/calendarLabels";
import { addDays } from "../../utils/dateUtils";
import { dateToISO, getMonthStart, getWeekStart, isSameDate } from "../../utils/date";

const MonthGrid = ({
  anchorDate,
  scheduledByDate,
  dragId,
  dropTarget,
  setDropTarget,
  onDrop,
  onRemove,
  onDragStart,
  onDragEnd,
  onOpenMilestone,
  executeMode = false,
  onMarkDone,
}: any) => {
  const monthStart = getMonthStart(anchorDate);
  const gridStart = getWeekStart(monthStart);
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  const currentMonth = monthStart.getMonth();

  return (
    <>
      <div
        className="grid grid-cols-7 bg-[rgba(0,0,0,0.015)]"
        style={{ borderBottom: `1px solid ${colors.borderLight}` }}
      >
        {DAY_ABBREVS.map((dayAbbrev) => (
          <div
            key={dayAbbrev}
            className="py-2 text-center text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ color: colors.textTertiary }}
          >
            {dayAbbrev}
          </div>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridAutoRows: "minmax(80px, 1fr)",
          flex: 1,
        }}
      >
        {days.map((day) => {
          const iso = dateToISO(day);
          const isCurrentMonth = day.getMonth() === currentMonth;
          const isToday = isSameDate(day, new Date());
          const isDropping = dropTarget === iso;
          const items = scheduledByDate[iso] || [];
          return (
            <div
              key={iso}
              onDragOver={executeMode ? undefined : (event) => { event.preventDefault(); setDropTarget(iso); }}
              onDragLeave={executeMode ? undefined : () => setDropTarget((previous) => (previous === iso ? null : previous))}
              onDrop={executeMode ? undefined : (event) => onDrop(event, iso)}
              style={{
                borderBottom: `1px solid ${colors.borderLight}`,
                borderRight: `1px solid ${colors.borderLight}`,
                padding: 6,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                overflow: "hidden",
                transition: "background 0.15s",
                background: isDropping
                  ? "rgba(0,136,255,0.05)"
                  : isCurrentMonth
                    ? "transparent"
                    : "rgba(0,0,0,0.015)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {isToday ? (
                  <span
                    style={{
                      background: colors.brandPrimary,
                      color: "#fff",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {day.getDate()}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      paddingLeft: 4,
                      color: isCurrentMonth ? colors.textPrimary : colors.textTertiary,
                    }}
                  >
                    {day.getDate()}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, overflow: "hidden" }}>
                {/* Mobile: colored dots per milestone */}
                <div className="flex flex-wrap gap-0.5 sm:hidden">
                  {items.slice(0, 5).map(({ dream, milestone }) => (
                    <span
                      key={milestone.id}
                      style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: dream.color || colors.brandPrimary, flexShrink: 0 }}
                    />
                  ))}
                  {items.length > 5 && (
                    <span style={{ fontSize: 8, color: colors.textTertiary, lineHeight: "6px" }}>+{items.length - 5}</span>
                  )}
                </div>
                {items.slice(0, 3).map(({ dream, goal, milestone }) => {
                  const chipColor = dream.color || "#0088ff";
                  const isDone = !!(milestone as any).completed;
                  return (
                    <div
                      key={milestone.id}
                      className="hidden sm:flex items-center gap-1"
                      title={`${dream.title} · ${goal.title} — ${milestone.title}`}
                      draggable={!executeMode && !isDone}
                      onDragStart={executeMode || isDone ? undefined : (event) => onDragStart(event, milestone.id)}
                      onDragEnd={executeMode || isDone ? undefined : onDragEnd}
                      style={{
                        background: isDone ? "rgba(0,0,0,0.03)" : `${chipColor}12`,
                        border: `1px solid ${isDone ? "rgba(0,0,0,0.08)" : `${chipColor}35`}`,
                        borderLeft: `3px solid ${isDone ? "rgba(0,0,0,0.15)" : chipColor}`,
                        borderRadius: 4,
                        padding: "2px 6px",
                        cursor: executeMode ? "pointer" : isDone ? "default" : "grab",
                        opacity: isDone ? 0.65 : 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: isDone ? colors.textTertiary : colors.textPrimary,
                          textDecoration: isDone ? "line-through" : "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {milestone.title}
                      </span>
                      <button
                        type="button"
                        onClick={() => onOpenMilestone({ iso, dream, goal, milestone })}
                        title="View milestone details"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: 20, height: 20, color: colors.textTertiary,
                          background: "#ffffff", border: `1px solid ${colors.borderLight}`,
                          cursor: "pointer", borderRadius: 6, flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.brandPrimary; (e.currentTarget as HTMLButtonElement).style.background = "rgba(56,189,248,0.1)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textTertiary; (e.currentTarget as HTMLButtonElement).style.background = "#ffffff"; }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                          <path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </button>
                      {isDone ? (
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e", flexShrink: 0 }}>✓</span>
                      ) : executeMode ? (
                        <button
                          type="button"
                          onClick={() => onMarkDone?.({ iso, dream, goal, milestone })}
                          title="Mark as done"
                          style={{
                            fontSize: 10, fontWeight: 700, color: "#16a34a",
                            background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.25)",
                            cursor: "pointer", padding: "1px 4px", borderRadius: 4, flexShrink: 0, lineHeight: 1.4,
                          }}
                        >✓</button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onRemove(milestone.id)}
                          title="Remove from calendar"
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: colors.textTertiary,
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: "1px 3px",
                            borderRadius: 4,
                            flexShrink: 0,
                            lineHeight: 1,
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#ef4444"; (e.currentTarget as HTMLButtonElement).style.background = "#fef2f2"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textTertiary; (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
                {items.length > 3 && (
                  <p className="hidden sm:block" style={{ margin: 0, fontSize: 10, color: colors.textTertiary, paddingLeft: 4 }}>
                    +{items.length - 3} more
                  </p>
                )}
                {!executeMode && isDropping && dragId && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      borderRadius: 4,
                      border: "2px dashed rgba(0,136,255,0.3)",
                      fontSize: 10,
                      color: "rgba(0,136,255,0.5)",
                      minHeight: 20,
                    }}
                  >
                    Drop
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MonthGrid;

